import React, { useState, useEffect } from 'react';
import { Layout, Typography, Button, Card, Result } from 'antd';
import { 
  QuestionCircleOutlined, 
  CheckOutlined, 
  CloseOutlined, 
  CheckCircleOutlined 
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import ScholarSidebar from '../components/ScholarSidebar';
import '../styles/ScholarAcknowledgementConfirmation.css';
import { getToken, getRequester } from '../utilities/commons';
import { postRequest } from '../utilities/apicalls';
import ToastMessage from '../components/ToastMessage';

const { Content } = Layout;
const { Text, Paragraph } = Typography;

/**
 * ScholarAcknowledgementConfirmation Component
 * 
 * Displays a confirmation dialog asking if the scholar wants to submit the receipt
 * @returns {JSX.Element} The ScholarAcknowledgementConfirmation component
 */
const ScholarAcknowledgementConfirmation = () => {
    const history = useHistory();
    const location = useLocation();
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);

    // Get receipt data from location state - fix the property name
    const receipt = location.state?.receiptDetails || {};
    
    // Automatically redirect to receipt list after successful submission
    useEffect(() => {
        if (confirmed) {
            // Delay redirect to show success message briefly
            const redirectTimer = setTimeout(() => {
                history.push('/scholar-acknowledgement-receipt');
            }, 1500); // 1.5 second delay
            
            // Cleanup timer if component unmounts
            return () => clearTimeout(redirectTimer);
        }
    }, [confirmed, history]);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            // Check if we have the necessary data
            if (!receipt || !receipt.id) {
                // console.error("Missing receipt data:", receipt);
                ToastMessage.error("Receipt data is missing. Please try again.");
                setLoading(false);
                return;
            }

            // Get authentication data
            const token = getToken() || localStorage.getItem('token');
            const scholarId = getRequester() || localStorage.getItem('id');

            if (!token || !scholarId) {
                ToastMessage.error("Authentication data missing. Please log in again.");
                setLoading(false);
                return;
            }

            // Create FormData object for the API request
            const formData = new FormData();
            formData.append('receipt_id', receipt.id);
            formData.append('token', token);
            formData.append('requester', scholarId);
            formData.append('status', 'Signed');
            
            // Also send the signature data - critical for the receipt update!
            if (receipt.signatureData) {
                formData.append('signature', receipt.signatureData);
            } else {
                // console.error("Missing signature data");
                ToastMessage.error("Signature data is missing. Please try again.");
                setLoading(false);
                return;
            }

            // console.log("Submitting receipt:", receipt.id);
            
            // Use postRequest with FormData flags
            const response = await postRequest('receipts/update', formData, true, true);

            // console.log('API Response:', response);

            if (response.status === 200) {
                setConfirmed(true);
            } else {
                // Handle non-200 response
                const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to submit receipt. Please try again.';
                ToastMessage.error(errorMessage);
            }
        } catch (err) {
            // console.error('Error confirming receipt:', err);
            const errorMessage = err?.messages?.error_messages?.[0] || 'Failed to submit receipt. Please try again.';
            ToastMessage.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Navigate back to the details page
    const handleCancel = () => {
        history.goBack();
    };

    return (
        <div className="page-wrapper">
            <Layout className="page-body">
                <ScholarSidebar activePage="acknowledgement-receipt" />
                <Content className="main-content">
                    <Card className="ac-confirmation-card">
                        <Result
                            status={confirmed ? "success" : "info"}
                            icon={confirmed ? 
                                <CheckCircleOutlined className="ac-confirmation-icon ac-success-icon" /> : 
                                <QuestionCircleOutlined className="ac-confirmation-icon ac-question-icon" />
                            }
                            title={confirmed ? "Receipt Successfully Submitted!" : "Submit Acknowledgement Receipt?"}
                            subTitle={
                                <div className="ac-confirmation-subtitle">
                                    <Paragraph style={{ textAlign: 'center' }}>
                                        {confirmed ? 
                                            "Your acknowledgement receipt has been successfully submitted." : 
                                            "Are you sure you want to submit this acknowledgement receipt?"
                                        }
                                    </Paragraph>
                                </div>
                            }
                            extra={!confirmed ? [
                                <div key="confirmation-buttons" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                    <Button 
                                        key="cancel" 
                                        icon={<CloseOutlined />}
                                        onClick={handleCancel}
                                        className="ac-confirmation-btn ac-secondary-btn"
                                        disabled={loading}
                                        style={{ margin: '0 8px' }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="primary" 
                                        key="submit" 
                                        icon={<CheckOutlined />}
                                        onClick={handleConfirm}
                                        className="ac-confirmation-btn"
                                        loading={loading}
                                        disabled={loading}
                                        style={{ margin: '0 8px' }}
                                    >
                                        Yes, Submit
                                    </Button>
                                </div>
                            ] : null}
                        />
                    </Card>
                    
                    <div className="ac-company-branding">
                        <Text className="ac-company-text">©2025 MYT Softdev Solutions Inc.</Text>
                    </div>
                </Content>
            </Layout>
        </div>
    );
};

export default ScholarAcknowledgementConfirmation; 